import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalysis } from '../contexts/AnalysisContext';
import './Pages.css';

const MAX_RESUMES = 5;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB — must match backend FAIR_USE.maxFileSizeBytes
const ACCEPTED_MIME_TYPES = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);
const ACCEPTED_EXT_RE = /\.(pdf|docx)$/i;

export const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { setJobDescription, setFiles, setSessionId, setLoading, setError } = useAnalysis();
  
  const [localJobDescription, setLocalJobDescription] = useState('');
  const [localFiles, setLocalFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);

  // Generate unique session ID
  const generateSessionId = () => {
    return `analysis_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Scroll to and focus the submit button for better UX
  const scrollToAndFocusSubmitButton = () => {
    setTimeout(() => {
      const submitButton = document.querySelector('.submit-button') as HTMLButtonElement;
      if (submitButton) {
        submitButton.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest' 
        });
        submitButton.focus();
      }
    }, 100); // Small delay to ensure DOM updates are complete
  };

  // Handle file selection
  const handleFileChange = useCallback((files: FileList | null) => {
    if (files) {
      const rejected: string[] = [];
      const fileArray = Array.from(files).filter((file) => {
        const okType = ACCEPTED_MIME_TYPES.has(file.type) || ACCEPTED_EXT_RE.test(file.name);
        const okSize = file.size <= MAX_FILE_SIZE_BYTES;
        if (!okType) rejected.push(`${file.name}: unsupported file type (PDF, DOCX only)`);
        else if (!okSize) rejected.push(`${file.name}: exceeds 10 MB limit`);
        return okType && okSize;
      });
      if (rejected.length) {
        // eslint-disable-next-line no-alert
        alert('Some files were rejected:\n\n' + rejected.join('\n'));
      }
      setLocalFiles((prev) => {
        const combined = [...prev, ...fileArray];
        // Cap at MAX_RESUMES, keeping the first selected + newest additions
        return combined.slice(0, MAX_RESUMES);
      });

      // Scroll to and focus the submit button when files are added
      if (fileArray.length > 0) {
        scrollToAndFocusSubmitButton();
      }
    }
  }, []);

  // Handle drag and drop
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    handleFileChange(files);
  }, [handleFileChange]);

  // Remove file
  const removeFile = (index: number) => {
    setLocalFiles(prev => {
      const updatedFiles = prev.filter((_, i) => i !== index);
      // Scroll to and focus the submit button after file removal if files still exist
      if (updatedFiles.length > 0 && localJobDescription.trim()) {
        scrollToAndFocusSubmitButton();
      }
      return updatedFiles;
    });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!localJobDescription.trim() || localFiles.length === 0) {
      setError('Please provide a job description and upload at least one resume.');
      return;
    }

    // Generate session ID and set state
    const sessionId = generateSessionId();
    setSessionId(sessionId);
    setJobDescription(localJobDescription);
    setFiles(localFiles);
    setLoading(true);
    setError(null);

    // Navigate to processing page
    navigate('/processing');
  };

  return (
    <div className="upload-page">

      <main className="upload-main">
        <div className="upload-container">
          <form onSubmit={handleSubmit} className="upload-form">
            {/* Job Description Section */}
            <div className="form-section">
              <label className="form-label">
                Job Description
              </label>
              <textarea
                id="jobDescription"
                value={localJobDescription}
                onChange={(e) => {
                  setLocalJobDescription(e.target.value);
                  if (e.target.value.trim().length > 0 && localFiles.length > 0) {
                    scrollToAndFocusSubmitButton();
                  }
                }}
                placeholder="Paste the job description here..."
                className="job-description-input"
                rows={6}
                required
              />
              <div className="char-count">
                {localJobDescription.length} characters
              </div>
            </div>

            {/* File Upload Section */}
            <div className="form-section">
              <label className="form-label">Resume Files</label>
              <div
                className={`file-upload-area ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  multiple
                  accept=".pdf,.docx"
                  onChange={(e) => { handleFileChange(e.target.files); e.target.value = ''; }}
                  className="file-input"
                  id="resume-files"
                />
                <label htmlFor="resume-files" className="file-upload-label">
                  <div className="upload-icon">📁</div>
                  <div className="upload-text">
                    <strong>Choose files</strong> or drag and drop
                  </div>
                  <div className="upload-hint">
                    PDF or DOCX - up to 10 MB / 5 pages each
                  </div>
                </label>
              </div>

              {/* File List */}
              {localFiles.length > 0 && (
                <div className="file-list">
                  <h4>Selected Files ({localFiles.length}/{MAX_RESUMES})</h4>
                  {localFiles.length > MAX_RESUMES && (
                    <div className="file-limit-warning">
                      ⚠️ Free tier allows up to {MAX_RESUMES} resumes. Please remove {localFiles.length - MAX_RESUMES} file{localFiles.length - MAX_RESUMES > 1 ? 's' : ''} to continue.
                    </div>
                  )}
                  {localFiles.map((file, index) => (
                    <div key={index} className="file-item">
                      <div className="file-details">
                        <div className="file-name">{file.name}</div>
                        <div className="file-size">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="file-remove"
                        aria-label={`Remove ${file.name}`}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>


            {/* Submit Button */}
            <div className="form-actions">
              <button
                type="submit"
                className="submit-button"
                disabled={!localJobDescription.trim() || localFiles.length === 0 || localFiles.length > MAX_RESUMES}
              >
                {localFiles.length > MAX_RESUMES ? `Remove ${localFiles.length - MAX_RESUMES} file${localFiles.length - MAX_RESUMES > 1 ? 's' : ''} to continue` : 'Quick Screen'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};